const DEFAULT_RETRY_OPTIONS = {
    maxRetries: 3,
    initialDelay: 1000,
    backoffMultiplier: 2,
};

export interface RetryOptions {
    maxRetries?: number;
    initialDelay?: number;
    backoffMultiplier?: number;
}

interface RequestError {
    status?: number;
    code?: number | string;
    statusCode?: number;
}

/**
 * Firestore / Firebase Functions error codes that retrying can never fix. They are
 * strings ('permission-denied', or 'functions/permission-denied' from a callable),
 * so the numeric 401/403/404 check alone let a denied page load retry three times
 * and show its error ~7 s late (Batch G item 7, 2026-09-29).
 */
const NON_RETRYABLE_CODES = new Set([
    'permission-denied',
    'unauthenticated',
    'not-found',
    'invalid-argument',
    'failed-precondition',
    'already-exists',
    'out-of-range',
    'unimplemented',
]);

function isNonRetryable(error: RequestError): boolean {
    const status = error.status || error.code || error.statusCode;
    if (status === 401 || status === 403 || status === 404) return true;
    return typeof error.code === 'string' && NON_RETRYABLE_CODES.has(error.code.replace(/^[a-z-]+\//, ''));
}

/**
 * Retries an async operation with exponential backoff.
 * Non-retryable errors (401, 403, 404, and the Firebase codes above) fail immediately.
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const { maxRetries, initialDelay, backoffMultiplier } = {
        ...DEFAULT_RETRY_OPTIONS,
        ...options,
    };

    let lastError: unknown;
    let delay = initialDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error: unknown) {
            lastError = error;

            // Do not retry for client/auth errors: they cannot succeed on a retry.
            if (error && typeof error === 'object' && isNonRetryable(error as RequestError)) {
                throw error;
            }

            if (attempt === maxRetries) {
                break;
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
            delay *= backoffMultiplier;
        }
    }

    throw lastError;
}

// Global map for in-flight requests
const inflightRequests = new Map<string, Promise<unknown>>();

/**
 * Clears all in-flight requests (useful for tests).
 */
export function clearInflightRequests(): void {
    inflightRequests.clear();
}

/**
 * Prevents multiple identical requests from firing simultaneously.
 * @param fn - The async function to execute
 * @param deps - Dependencies that define the request's uniqueness
 * @param key - Optional explicit key to use instead of deps
 */
export async function withDeduplication<T>(
    fn: () => Promise<T>,
    deps: unknown[],
    key?: string
): Promise<T> {
    // Use explicit key or stable key from deps
    const cacheKey = key || JSON.stringify(deps);

    const existing = inflightRequests.get(cacheKey);
    if (existing) {
        return existing as Promise<T>;
    }

    const promise = fn().finally(() => {
        inflightRequests.delete(cacheKey);
    });

    inflightRequests.set(cacheKey, promise);
    return promise;
}
